import { useAppConfig } from '#/config/AppConfigContext';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { salaryApiIsomorphicFetchJobSynonym } from '#/modules/salary/api/salaryApiFetchJobSynonym';
import { ClientOnlyLazySearchJobsRefinements } from '#/modules/search/jobs/components/Refinements/ClientOnlyLazySearchJobsRefinements';
import { searchJobsRoutePathByLocale } from '#/modules/search/jobs/routing/searchJobs.route-paths';
import type { searchJobsRouteReference } from '#/modules/search/routing/search.routes';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonAppRouteShouldRevalidateFunctionArgs } from '#/routing/types/HorizonAppRouteShouldRevalidateFunctionArgs';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SearchJobsPage } from './SearchJobsPage';

export const handle = {
  urlByLocale: searchJobsRoutePathByLocale,
  defaultSearchType: 'JOB',
  hideFooterDivider: true,
  searchFromPageType: 'SEARCH',
  showSearchRefinements: true,
  showDesktopSearch: true,
  searchRefinements: <ClientOnlyLazySearchJobsRefinements />,
  isSearchPage: true,
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
}: HorizonAppRouteLoaderArgs<typeof searchJobsRouteReference>) {
  const url = new URL(request.url);

  const synonymId = url.searchParams.get('synonymId');

  const synonym = synonymId
    ? await salaryApiIsomorphicFetchJobSynonym({ request, context })(
        synonymId,
      ).catch(() => null)
    : null;

  return { synonym };
}

export function shouldRevalidate({
  currentUrl,
  defaultShouldRevalidate,
  nextUrl,
}: HorizonAppRouteShouldRevalidateFunctionArgs<
  typeof searchJobsRouteReference
>) {
  if (currentUrl.searchParams.size === nextUrl.searchParams.size) {
    return false;
  }

  return defaultShouldRevalidate;
}

export default function SearchJobsHorizonRoute(
  _: HorizonAppRouteComponentProps<typeof loader>,
) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const ogImage = (() => {
    switch (locale) {
      case I18nLocale.EN:
        return getStaticImageSrc(
          'og-image-en-jobs.jpg',
          appConfig.idealist.staticImgPath,
        );
      case I18nLocale.ES:
        return;
      case I18nLocale.PT:
        return getStaticImageSrc(
          'og-image-pt-jobs.jpg',
          appConfig.idealist.staticImgPath,
        );
    }
  })();

  return (
    <RouteModule
      title={
        /* TRANSLATORS: job search page title */
        i18n('Best Jobs Near You, Remote Jobs, Nonprofit Jobs')
      }
      description={
        /* TRANSLATORS: job search page description */
        i18n(
          'Join Idealist to find a social-impact job and take your career to the next level',
        )
      }
      ogImage={ogImage}
    >
      <SearchJobsPage />
    </RouteModule>
  );
}
